import { useCallback, useState } from 'react';
import { useAuth } from '~/hooks/useAuth';
import type { Claim, GlassArea, ImageInput } from '~/lib/api';
import { api, JOURNEY_TYPE, uploadToSignedUrl } from '~/lib/api';
import { claimHandedness, type DriverSide } from '~/lib/handedness';
import { uniqueId } from '~/lib/id';
import { logger } from '~/lib/logger';
import { brandingPhoto, type Photo, photosForArea } from '~/lib/photos';

/**
 * Submits a journey. Extracted from SubmitScreen so the Review screen owns the send and
 * the capture screens stay about capturing.
 *
 * The idempotency key is minted ONCE per hook instance, not per attempt. Regenerating it
 * on retry would make a resubmit look like a brand new job to the server, which defeats
 * the only reason to send one — a dropped connection would then bill twice and produce
 * two verdicts for one inspection.
 */
export interface SubmitState {
  busy: boolean;
  error: string | null;
  /** Fraction complete per photo id, for the web signed-URL path. */
  progress: Record<string, number>;
}

export function useJobSubmit() {
  const auth = useAuth();
  const [state, setState] = useState<SubmitState>({ busy: false, error: null, progress: {} });
  const [idempotencyKey, setIdempotencyKey] = useState(uniqueId);

  const submit = useCallback(
    async (opts: {
      areas: GlassArea[];
      photos: Photo[];
      vin?: string;
      /**
       * Where the driver sits, from the capture screen's handedness gate. Carried
       * through to `claim.handedness` because the backend decides DPVA laterality
       * from it — the gate already made the operator answer, and leaving that
       * answer on the client meant the model re-guessed it from a mirrored
       * exterior photo.
       */
      side?: DriverSide;
    }): Promise<string | null> => {
      setState({ busy: true, error: null, progress: {} });
      try {
        // Two source strategies, one per platform. Web mints a signed URL and PUTs the
        // bytes straight to storage; native sends base64 inline, because reading a
        // file:// URI back into an XHR body is the awkward part there and the API
        // already accepts and sniffs inline bytes.
        const attach = async (
          photo: Photo,
          role: ImageInput['role'],
          glassArea?: GlassArea,
        ): Promise<ImageInput> => {
          const base = {
            role,
            ...(glassArea ? { glass_area: glassArea } : {}),
            // The server has accepted this since 0014 and answers with `retake_shot`.
            // Sending it is what makes that answer point at something.
            ...(photo.shot ? { shot_type: photo.shot } : {}),
          };
          if (photo.base64) {
            setState((s) => ({ ...s, progress: { ...s.progress, [photo.id]: 1 } }));
            return { ...base, data: photo.base64 };
          }
          const signed = await api.requestUploadUrl({
            content_type: 'image/jpeg',
            filename: `${photo.id}.jpg`,
            role,
            glass_area: glassArea,
          });
          await uploadToSignedUrl(
            signed,
            { uri: photo.uri, name: `${photo.id}.jpg`, type: 'image/jpeg', size: photo.bytes },
            (f) => setState((s) => ({ ...s, progress: { ...s.progress, [photo.id]: f } })),
          );
          return { ...base, gcs_uri: signed.gcs_uri };
        };

        const images: ImageInput[] = [];
        for (const area of opts.areas) {
          // Every angle is submitted. The server groups them by area and hands all of
          // them to the adjudicator, so two frames that disagree escalate rather than
          // being averaged away.
          for (const photo of photosForArea(opts.photos, area)) {
            images.push(await attach(photo, 'glass_area', area));
          }
        }
        const branding = brandingPhoto(opts.photos);
        if (branding) {
          images.push(await attach(branding, 'logo_silkscreen'));
        }

        /*
          A label for a human reading the job, not an identity. The IDENTITY is the oid in
          the access token, which the API reads and stores as `operator_oid` — the client
          cannot influence that and should not try. Omitted when signed out so the server
          applies its own default ("api") rather than the client inventing one.
        */
        const submittedBy =
          auth.status === 'authenticated'
            ? auth.user.name?.trim() || auth.user.upn || undefined
            : undefined;

        const claim: Claim = {
          journey_type: JOURNEY_TYPE,
          ...(opts.vin?.trim() ? { vin: opts.vin.trim() } : {}),
          // Omitted rather than guessed when the gate was never answered — the
          // server then applies its market default, which is the same thing this
          // client would have to invent, only recorded honestly.
          ...(opts.side ? { handedness: claimHandedness(opts.side) } : {}),
          ...(submittedBy ? { submitted_by: submittedBy } : {}),
        };

        const resp = await api.submitJob({
          idempotency_key: idempotencyKey,
          claim,
          claimed_areas: opts.areas.map((a) => `GLASS|${a}`),
          images,
        });
        // Accepted, so the next journey is a different job.
        setIdempotencyKey(uniqueId());
        setState((s) => ({ ...s, busy: false }));
        return resp.job_id;
      } catch (e) {
        logger.error('[submit] failed', e);
        setState((s) => ({
          ...s,
          busy: false,
          error:
            e instanceof Error
              ? e.message
              : 'The send did not complete. Your photos are still here.',
        }));
        return null;
      }
    },
    [auth, idempotencyKey],
  );

  return { ...state, submit, idempotencyKey };
}
